import Head from 'next/head';
import { useRouter } from 'next/router';
import connectToDatabase from '../../lib/mongodb';
import mongoose from 'mongoose';
import { useState, useContext } from 'react';
import { UserContext } from '../../components/Navigation';
import Link from 'next/link';
import Icon from '../../components/Icon';
import { display } from '../../lib/fonts';
import { page as pageBg, card, btn, badge, muted, body, link } from '../../lib/ui';

export default function JobDetail({ job }) {
  const router = useRouter();
  const { user } = useContext(UserContext);

  if (!job) {
    return (
      <div className={`${pageBg} flex items-center justify-center px-4`}>
        <div className="text-center">
          <h1 className={`${display.className} text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white`}>Job Not Found</h1>
          <p className={`mx-auto mt-3 mb-8 max-w-md ${body}`}>The job you are looking for does not exist or has been removed.</p>
          <Link href="/job_search" className={btn.primary}>
            Back to Job Search
          </Link>
        </div>
      </div>
    );
  }

  const jobSchema = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "datePosted": job.createdAt,
    "validThrough": new Date(new Date(job.createdAt).getTime() + 1000 * 60 * 60 * 24 * 30).toISOString(),
    "employmentType": job.type,
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.company,
      "logo": "https://doogler.in/favicon.ico"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location,
        "addressCountry": "IN"
      }
    },
    "baseSalary": job.salary ? {
      "@type": "MonetaryAmount",
      "currency": "INR",
      "value": {
        "@type": "QuantitativeValue",
        "value": job.salary,
        "unitText": "YEAR"
      }
    } : undefined
  };

  return (
    <>
      <Head>
        <title>{`${job.title} at ${job.company} | Doogler`}</title>
        <meta name="description" content={`Apply for ${job.title} at ${job.company} in ${job.location}. ${job.description.substring(0, 160)}`} />
        <meta name="keywords" content={`${job.title}, ${job.company} jobs, jobs in ${job.location}, ${job.type} jobs, Doogler referral`} />
        <meta property="og:title" content={`${job.title} at ${job.company} | Doogler`} />
        <meta property="og:description" content={`Discover this ${job.type} opportunity in ${job.location}. Secure your next role through Doogler's referral network.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://doogler.in/jobs/${job.jobId || job._id}`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jobSchema) }}
        />
      </Head>
      <div className={`${pageBg} px-4 py-8 sm:px-6 sm:py-12 lg:px-8`}>
        <div className={`${card} mx-auto max-w-4xl overflow-hidden`}>
          <div className="p-6 sm:p-8 md:p-12">
            <nav className={`mb-5 ${muted}`} aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <li><Link href="/" className="hover:text-teal-700 dark:hover:text-customBlue">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li><Link href="/job_search" className="hover:text-teal-700 dark:hover:text-customBlue">Jobs</Link></li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="max-w-[200px] truncate text-slate-700 dark:text-slate-300">{job.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 flex-1">
                <h1 className={`${display.className} text-3xl md:text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white text-balance`}>{job.title}</h1>
                <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-slate-600 dark:text-slate-400">
                  <span className="inline-flex items-center gap-1.5"><Icon name="building" className="w-4 h-4 text-slate-400" />{job.company}</span>
                  <span className="inline-flex items-center gap-1.5"><Icon name="pin" className="w-4 h-4 text-slate-400" />{job.location}</span>
                  <span className={badge.brand}>{job.type}</span>
                </p>
              </div>
              <div className="w-full md:w-auto md:flex-shrink-0">
                {user ? (
                  <a href={job.applyLink} target="_blank" rel="noopener noreferrer" className={`${btn.primary} w-full min-h-[48px] md:px-10`}>
                    Apply Now
                  </a>
                ) : (
                  <Link href="/login?role=job-seeker" className={`${btn.primary} w-full min-h-[48px] md:px-10`}>
                    Login to Apply
                  </Link>
                )}
              </div>
            </div>

            <dl className="my-10 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-slate-200 dark:border-gray-800 bg-slate-200 dark:bg-gray-800 sm:grid-cols-3">
              {[
                { label: 'Salary estimate', value: job.salary || 'Competitive' },
                { label: 'Date posted', value: new Date(job.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) },
                { label: 'Experience', value: 'Freshers / Pro' },
              ].map((item) => (
                <div key={item.label} className="flex flex-col-reverse bg-white dark:bg-gray-900 px-5 py-4">
                  <dt className={`mt-1 ${muted}`}>{item.label}</dt>
                  <dd className="text-lg font-semibold text-slate-900 dark:text-white">{item.value}</dd>
                </div>
              ))}
            </dl>

            <div className="space-y-10">
              <section>
                <h2 className={`${display.className} mb-3 text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white`}>Job Description</h2>
                <div className="whitespace-pre-wrap text-base leading-relaxed text-slate-700 dark:text-slate-300">
                  {job.description}
                </div>
              </section>

              <section>
                <h2 className={`${display.className} mb-3 text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white`}>Requirements</h2>
                <div className="whitespace-pre-wrap text-base leading-relaxed text-slate-700 dark:text-slate-300">
                  {job.requirements}
                </div>
              </section>
            </div>

            <section className="mt-12 rounded-xl border border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-950/40 p-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Referrer advantage</h3>
              {user ? (
                <div className="flex items-center gap-4">
                  <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-customBlue text-xl font-bold text-slate-950">
                    {job.referrer?.name?.[0] || 'D'}
                  </span>
                  <div className="min-w-0">
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{job.referrer?.name || 'Anonymous Referrer'}</p>
                    <p className={body}>Verified employee at <strong className="font-semibold text-slate-900 dark:text-white">{job.referrer?.company}</strong></p>
                  </div>
                </div>
              ) : (
                <div>
                  <p className={`mb-4 ${body}`}>Want a direct referral? Log in to connect with the employee who posted this role.</p>
                  <Link href="/login?role=job-seeker" className={`${link} inline-flex min-h-[44px] items-center gap-1.5`}>
                    Unlock referrer details <Icon name="arrow" className="w-4 h-4" strokeWidth={2} />
                  </Link>
                </div>
              )}
            </section>

            <div className="mt-10">
              <Link href="/job_search" className={`${link} inline-flex min-h-[44px] items-center gap-1.5`}>
                <Icon name="arrowLeft" className="w-4 h-4" strokeWidth={2} /> Back to search results
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getServerSideProps(context) {
  const { id } = context.params;

  try {
    const connectToDatabase = (await import('../../lib/mongodb')).default;
    const mongoose = (await import('mongoose')).default;
    
    await connectToDatabase();
    const db = mongoose.connection.db;
    const jobsCollection = db.collection('jobs');

    let job = null;

    // Try finding by internal jobId first
    job = await jobsCollection.findOne({ jobId: id });

    // Fallback to MongoDB _id
    if (!job && mongoose.Types.ObjectId.isValid(id)) {
      job = await jobsCollection.findOne({ _id: new mongoose.Types.ObjectId(id) });
    }

    if (!job) {
      return { props: { job: null } };
    }

    // Sanitize job object for serialization
    const sanitizedJob = {
      ...job,
      _id: job._id.toString(),
      createdAt: (job.createdAt instanceof Date ? job.createdAt : new Date()).toISOString(),
      updatedAt: (job.updatedAt instanceof Date ? job.updatedAt : new Date()).toISOString(),
    };
    
    // Also remove any other non-serializable fields if they exist
    if (sanitizedJob.referrerId) sanitizedJob.referrerId = sanitizedJob.referrerId.toString();

    return {
      props: {
        job: JSON.parse(JSON.stringify(sanitizedJob))
      }
    };
  } catch (error) {
    console.error('Error fetching job in GSProps:', error);
    return { props: { job: null } };
  }
}
